package models

import kotlinx.serialization.KSerializer
import kotlinx.serialization.Serializable
import kotlinx.serialization.descriptors.PrimitiveKind
import kotlinx.serialization.descriptors.PrimitiveSerialDescriptor
import kotlinx.serialization.encoding.Decoder
import kotlinx.serialization.encoding.Encoder
import kotlin.random.Random

/**
 * An occasional per-round "curveball" that powers Ginko Mode. [NONE] is the standard Banko ruleset
 * and remains common even in Ginko games so the actual twists have contrast.
 *
 * Modifiers are plain data carried on [Round], so they persist and sync for free. The engine reads
 * them and stays pure — the only randomness is *picking* the modifier when a round opens, which the
 * orchestrator does via [pick] (injectable [Random] keeps it testable).
 */
@Serializable(with = RoundModifierSerializer::class)
enum class RoundModifier {
    /** Standard Banko rules. */
    NONE,

    /** Every normal points gain this round counts double. */
    DOUBLE_TROUBLE,

    /** The danger flips: 7s are safe and pay +70 all round, but doubles bust instead of doubling. */
    FLIPSIDE,

    /** The round opens with a pot already worth [JACKPOT_START_POINTS]. */
    JACKPOT,

    /** Rolling doubles triples the pot instead of doubling it. */
    HIGH_STAKES,

    /** Nobody may bank during the first three rolls — ride it out. */
    LOCKDOWN,

    /** Seasonal July 4 rules: safe 7s pay 76, and late doubles add a bonus burst. */
    FIREWORKS,

    /** Fall: the first three rolls each gain five extra points. */
    FALL_HARVEST,

    /** Halloween: late doubles double the pot and add a 31-point treat. */
    HALLOWEEN;

    /** Points the pot starts with when a round opens under this modifier. */
    fun startingPoints(): Int = if (this == JACKPOT) JACKPOT_START_POINTS else 0

    /** Whether banking is blocked given how many rolls have happened this round. */
    fun banksLockedAtRoll(currentRoll: Int): Boolean = this == LOCKDOWN && currentRoll <= SAFE_ROLLS

    companion object {
        const val JACKPOT_START_POINTS = 50

        /** Rolls during which a 7 normally can't bust (mirrors BankoGameEngine). */
        const val SAFE_ROLLS = 3

        /** The round-level modifiers Ginko Mode draws from — deliberately excludes [NONE]. */
        val CHAOS: List<RoundModifier> =
            listOf(FLIPSIDE, LOCKDOWN)

        fun pickChaos(random: Random = Random.Default): RoundModifier =
            CHAOS[random.nextInt(CHAOS.size)]

        /** A random modifier sometimes when Ginko Mode is [enabled], otherwise [NONE]. */
        fun pick(
            enabled: Boolean,
            random: Random = Random.Default,
            chancePercent: Int = 35,
        ): RoundModifier {
            if (!enabled) return NONE
            val clampedChance = chancePercent.coerceIn(0, 100)
            return if (random.nextInt(100) < clampedChance) pickChaos(random) else NONE
        }
    }
}

object RoundModifierSerializer : KSerializer<RoundModifier> {
    override val descriptor = PrimitiveSerialDescriptor("RoundModifier", PrimitiveKind.STRING)

    override fun deserialize(decoder: Decoder): RoundModifier {
        val value = decoder.decodeString()
        return RoundModifier.entries.firstOrNull { it.name == value } ?: RoundModifier.NONE
    }

    override fun serialize(encoder: Encoder, value: RoundModifier) {
        encoder.encodeString(value.name)
    }
}
